import { StringFormat } from "@packages/common";
import type { FilterDTO } from "../../search";

export abstract class OptionalTransformer {
  private static readonly mappedValues: Record<string, string> = {
    "controle automático de velocidade": "Piloto Automático",
    "direção hidráulica": "Direção hidráulica/elétrica",
    "farol de xenônio": "Faróis em LED/Xenon",
  };

  private static readonly categories: Record<string, string[]> = {
    "Conforto & Conveniência": [
      "Ar condicionado",
      "Bancos em couro",
      "Direção hidráulica/elétrica",
      "Piloto Automático",
      "Piloto Automático Adaptativo (ACC)",
      "Retrovisores elétricos",
      "Vidros elétricos",
      "Travas elétricas",
    ],
    Segurança: [
      "Airbag",
      "Controle de tração",
      "Freio ABS",
      "Controle de estabilidade",
      "Frenagem autônoma",
    ],
    "Tecnologia & Conectividade": [
      "Central Multimídia",
      "Espelhamento com Smartphone",
      "WiFi",
      "Chave presencial",
      "Sensor de estacionamento",
      "Câmera de ré",
    ],
    Outros: [
      "Faróis em LED/Xenon",
      "Rodas de liga-leve",
      "Teto solar",
      "Tração 4x4",
    ],
  };

  /**
   * Transforms a given `FilterDTO` for optional features by mapping known labels
   * to preferred display names and sorting the items alphabetically by label.
   *
   * @param {FilterDTO} filter - The filter object to transform.
   * @returns {FilterDTO} - The transformed filter object with mapped and sorted items.
   */
  static transform(filter: FilterDTO): FilterDTO {
    if (!filter || filter.name !== "OP") {
      return filter;
    }

    const transformedItems = filter.items.map((item) => {
      const normalizedLabel = item.name.toLocaleLowerCase();
      const mappedLabel =
        OptionalTransformer.mappedValues[normalizedLabel] ?? item.name;

      return {
        ...item,
        label: mappedLabel,
      };
    });

    const categorizedItems = Object.entries(OptionalTransformer.categories)
      .map(([category, itemLabels]) => {
        const itemsInCategory = transformedItems
          .filter((item) =>
            itemLabels.some(
              (label) =>
                StringFormat.formatSlug(label) ===
                StringFormat.formatSlug(item.label)
            )
          )
          .map((item) => ({
            label: item.label,
            name: item.name,
            count: item.count,
            items: [],
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        return {
          label: category,
          name: category,
          count: itemsInCategory.length,
          items: itemsInCategory,
        };
      })
      .filter((category) => category.items.length > 0);

    return {
      ...filter,
      items: categorizedItems,
    };
  }

  /**
   * Transforms an array of optional feature strings from URL parameters by mapping known
   * optional features to their preferred display labels. Unmapped values retain their original labels.
   *
   * @param {string[]} optionals - The list of optional features from URL parameters.
   * @returns {Array<{ value: string, label: string }>} - An array of objects with `value` and mapped `label` for each optional feature.
   */
  static transformFromUrl(
    optionals: string[]
  ): Array<{ value: string; label: string }> {
    return optionals.map((optional) => {
      const value = optional.toLocaleLowerCase();
      return {
        value: optional,
        label: OptionalTransformer.mappedValues[value] ?? optional,
      };
    });
  }
}
